import { ReactElement } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { CircularProgress, Stack, Typography } from '@mui/material'
import { PopoverPanel } from '@ui2/structure'
import type { ProjectEditDTO } from 'src/state/api/generated/fastAPI.schemas'
import { useGetProjectEdits } from 'src/state/api/generated/project-edits/project-edits'

dayjs.extend(utc)

const formatUser = (edit: ProjectEditDTO): string => {
  const name = edit.user_name || edit.user_email || edit.user_guid || 'Unbekannter Benutzer'
  return edit.is_ai ? `${name} (ai)` : name
}

interface Props {
  anchorEl: HTMLElement | null
  onClose: () => void
  projectGuid: string | null
}

const EditHistoryPopover = ({ anchorEl, onClose, projectGuid }: Props): ReactElement => {
  const { data, isLoading } = useGetProjectEdits(projectGuid ?? '', {
    query: { enabled: Boolean(anchorEl) && Boolean(projectGuid) },
  })

  return (
    <PopoverPanel
      anchorEl={anchorEl}
      onClose={onClose}
      title="Bearbeitungsverlauf"
      dataCy="project-edit-history-popover"
    >
      <Stack spacing={1} minWidth={260} maxHeight={320} sx={{ overflowY: 'auto' }}>
        {isLoading && (
          <Stack alignItems="center" py={1}>
            <CircularProgress size={20} />
          </Stack>
        )}
        {!isLoading && data?.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Keine Bearbeitungshistorie vorhanden
          </Typography>
        )}
        {!isLoading &&
          data?.map((edit: ProjectEditDTO, index: number) => (
            <Stack
              key={`${edit.timestamp}-${index}`}
              direction="row"
              justifyContent="space-between"
              gap={2}
            >
              <Typography variant="body2" color="text.secondary" whiteSpace="nowrap">
                {dayjs.utc(edit.timestamp).local().format('DD.MM.YYYY HH:mm:ss')}
              </Typography>
              <Typography variant="body2" textAlign="right">
                {formatUser(edit)}
              </Typography>
            </Stack>
          ))}
      </Stack>
    </PopoverPanel>
  )
}

export default EditHistoryPopover
